// Presentation layer for the Rupiah wallet + golongan surfaces. Kept apart from
// billing.format.js because that file speaks the quota-unit / invoice
// vocabulary, and the two must not blur together.

/** Rupiah, no decimals. `Rp64.240.600` — id-ID grouping, no space, per design. */
export function formatRp(value) {
  return `Rp${Number(value || 0).toLocaleString('id-ID')}`
}

/** `7 August 2026`. Returns null (not a dash) so callers can hide the row. */
export function formatLongDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

// ─── Golongan usaha ──────────────────────────────────────────────────────────

export const GOLONGAN_LABEL = {
  korporat: 'Corporate',
  umkm: 'UMKM',
}

export const golonganLabel = (g) => GOLONGAN_LABEL[g] || g || '—'

// Review lifecycle of a UMKM application. `unverified` is the resting state for
// a company that never applied — it is NOT a rejection and must not read as one.
export const GOLONGAN_REVIEW_BADGE = {
  pending_review: {
    cls: 'bg-warning/50 border-warning/200 text-warning/700',
    label: 'Waiting for review',
  },
  in_review: {
    cls: 'bg-blue/50 border-blue/200 text-blue/700',
    label: 'In review',
  },
  // 'verified', not 'approved' — that is the value the CHECK constraint on
  // companies.golongan_review_status allows and the one local-golongan writes.
  verified: {
    cls: 'bg-success/50 border-success/200 text-success/700',
    label: 'Approved',
  },
  rejected: {
    cls: 'bg-error/50 border-error/200 text-error/700',
    label: 'Rejected',
  },
}

export const golonganReviewBadge = (status) => GOLONGAN_REVIEW_BADGE[status] || null

/** An application is in flight — the company still pays Korporat rates. */
export const isGolonganPending = (s) => s === 'pending_review' || s === 'in_review'

// ─── Package / subscription ──────────────────────────────────────────────────

// Registry tiers (pricing_packages.tier), as seeded. `payg` is the UMKM floor:
// no period, no subscription fee — pay per enquiry only. Fallback ONLY: the
// registry carries a display `name` and that always wins, so a tier added to
// the DB without a line here degrades to its raw key rather than a wrong label.
export const TIER_LABEL = {
  payg: 'Pay as You Go',
  starter: 'Starter',
  growth: 'Growth',
  business: 'Business',
}

export function packageLabel(pkg) {
  if (!pkg) return null
  return pkg.name || TIER_LABEL[pkg.tier] || pkg.tier || null
}

// ─── Wallet ledger ───────────────────────────────────────────────────────────

// wallet_transactions.type → human category. Matches the "Category" column.
//
// `spending` keeps a label even though the statement never lists one: the server
// hides the settlement leg of a hold (STATEMENT RULE in
// core/service/wallet/tx-presentation.js) because it moves no spendable saldo,
// and a completed enquiry is represented by its `hold` row — which is why `hold`
// reads "Credit report" and not "Reservation".
export const TX_TYPE_LABEL = {
  topup: 'Top up',
  spending: 'Credit report',
  hold: 'Credit report',
  release: 'Credit report',
  renewal_charge: 'Plan renewal',
  subscription_fee: 'Subscription fee',
  expiry_forfeit: 'Forfeited saldo',
  manual_adjust: 'Adjustment',
}

// A phone-number lookup moves saldo through the same hold / spending / release
// legs as an enquiry, so `type` alone would file it under "Credit report". The
// ledger's `source_type` tells them apart.
const TX_SOURCE_LABEL = {
  getcontact: 'Phone number check',
}

export const txTypeLabel = (type, sourceType) =>
  TX_SOURCE_LABEL[sourceType] || TX_TYPE_LABEL[type] || type || '—'

// Derived server-side (core/service/wallet/tx-presentation.js). `pending` has no
// ledger row — an unpaid top-up is an invoice — so that tab is empty by
// construction, not pending a feature. Rupiah top-up already exists.
export const TX_STATUS_BADGE = {
  success: { cls: 'bg-success/50 border-success/200 text-success/700', label: 'Success' },
  pending: { cls: 'bg-warning/50 border-warning/200 text-warning/700', label: 'Pending' },
  canceled: { cls: 'bg-error/50 border-error/200 text-error/700', label: 'Canceled' },
  on_hold: { cls: 'bg-gray/100 border-gray/200 text-gray/700', label: 'On hold' },
}

export const txStatusBadge = (s) =>
  TX_STATUS_BADGE[s] || { cls: 'bg-gray/50 border-gray/200 text-gray/700', label: s || '—' }

/**
 * Optional second line under the Category, returned as null when a row needs no
 * qualifier.
 *
 * The three outcomes of a hold, and what the statement actually shows for each
 * (the STATEMENT RULE in core/service/wallet/tx-presentation.js decides which
 * rows arrive here at all):
 *
 *   STILL HELD    one row, the `hold`.      -> 'Funds on hold'
 *   RELEASED      two rows, `hold` + `release`, opposite signs, both "Canceled".
 *                 Only the `release` leg is labelled 'Funds returned'; the
 *                 `hold` leg gets NO label, because the returning leg is the one
 *                 that needs explaining — an unlabelled debit followed by a
 *                 labelled credit already reads as a reservation and its refund.
 *   CAPTURED      ONE row, the `hold`. Its `spending` leg moved no spendable
 *                 saldo and is not listed, so there is no pair to tell apart and
 *                 no qualifier to add: the row is simply the charge.
 *
 * That last line is why this returns null for a captured hold rather than
 * something like 'Funds used'. A lone row labelled as one leg of something
 * invites the reader to hunt for the other leg, which is precisely the
 * double-charge misreading the STATEMENT RULE was written to end.
 */
export function txLegLabel(tx) {
  if (!tx) return null
  if (tx.type === 'release') return 'Funds returned'
  if (tx.type === 'hold' && tx.status === 'on_hold') return 'Funds on hold'
  return null
}

// Tabs, in the design's order. Value `null` = no filter.
export const TX_STATUS_TABS = [
  { value: null, label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'pending', label: 'Pending' },
  { value: 'canceled', label: 'Cancelled' },
  { value: 'on_hold', label: 'On hold' },
]

/**
 * Direction of a movement from the CUSTOMER's point of view: -1 money out,
 * +1 money in.
 *
 * Reads `balance_delta_idr`, which is non-zero on every row the statement lists:
 * the one type whose balance_delta is 0 — `spending`, the settlement leg of a
 * hold — is not listed at all (STATEMENT RULE in
 * core/service/wallet/tx-presentation.js).
 *
 * The held-side fallback stays for rows that arrive OUTSIDE the statement: a
 * capture response hands back its `spending` row, and signing that off the
 * balance alone would paint an outgoing charge as money coming in.
 */
export function txSign(tx) {
  const balance = Number(tx?.balance_delta_idr || 0)
  if (balance !== 0) return balance > 0 ? 1 : -1
  return Number(tx?.held_delta_idr || 0) < 0 ? -1 : 1
}

/** `+Rp50.000` / `-Rp361.000`, signed for display only — amount_idr is unsigned. */
export function formatSignedRp(tx) {
  const sign = txSign(tx)
  return `${sign < 0 ? '-' : ''}${formatRp(tx?.amount_idr)}`
}

/** `22 Jun 2026` + `13:50`, the design's two-line date cell. */
export function formatDateTimeParts(iso) {
  if (!iso) return { date: '—', time: '' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' }
  return {
    date: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  }
}
